import type { Keyboard, KeyboardLayout, KeyGeometry } from "@/domain/types";
import { assignSplitRegions, normalizeOrigin, verticalGaps } from "@/hardware/geometry";
import { QWERTY_CODE_TO_CHAR } from "@/hardware/keyboard-layouts/qwerty";

/** A KLE property object: relative cursor nudges and rotation. Other props (a, n, f, ...) are ignored. */
export interface KLEProps {
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  r?: number;
  rx?: number;
  ry?: number;
  [extra: string]: unknown;
}
export type KLEItem = string | KLEProps;
export type KLERow = KLEItem[];
/** A KLE document: an optional leading metadata object, then rows of items. */
export type KLEData = (KLERow | Record<string, unknown>)[];

// Legend (display text) to KeyboardEvent.code. Ported from the prototype's kl-import.js L2C table.
const L2C: Record<string, string> = {
  esc: "Escape",
  escape: "Escape",
  tab: "Tab",
  caps: "CapsLock",
  "caps lock": "CapsLock",
  capslock: "CapsLock",
  shift: "ShiftLeft",
  lshift: "ShiftLeft",
  "l shift": "ShiftLeft",
  rshift: "ShiftRight",
  "r shift": "ShiftRight",
  "⇧": "ShiftLeft",
  ctrl: "ControlLeft",
  control: "ControlLeft",
  lctrl: "ControlLeft",
  rctrl: "ControlRight",
  "⌃": "ControlLeft",
  alt: "AltLeft",
  lalt: "AltLeft",
  ralt: "AltRight",
  altgr: "AltRight",
  opt: "AltLeft",
  option: "AltLeft",
  "⌥": "AltLeft",
  win: "MetaLeft",
  gui: "MetaLeft",
  super: "MetaLeft",
  cmd: "MetaLeft",
  command: "MetaLeft",
  meta: "MetaLeft",
  "⌘": "MetaLeft",
  "⊞": "MetaLeft",
  enter: "Enter",
  return: "Enter",
  ent: "Enter",
  "↵": "Enter",
  "⏎": "Enter",
  space: "Space",
  spc: "Space",
  spacebar: "Space",
  bksp: "Backspace",
  bspc: "Backspace",
  backspace: "Backspace",
  "back space": "Backspace",
  "⌫": "Backspace",
  del: "Delete",
  delete: "Delete",
  "⌦": "Delete",
  ins: "Insert",
  insert: "Insert",
  home: "Home",
  end: "End",
  pgup: "PageUp",
  "page up": "PageUp",
  pgdn: "PageDown",
  "page down": "PageDown",
  up: "ArrowUp",
  "↑": "ArrowUp",
  down: "ArrowDown",
  "↓": "ArrowDown",
  "⇩": "ArrowDown",
  left: "ArrowLeft",
  "←": "ArrowLeft",
  "⇦": "ArrowLeft",
  right: "ArrowRight",
  "→": "ArrowRight",
  "⇨": "ArrowRight",
  menu: "ContextMenu",
  app: "ContextMenu",
  "num lock": "NumLock",
  numlock: "NumLock",
  "scroll lock": "ScrollLock",
  scrlk: "ScrollLock",
  prtsc: "PrintScreen",
  printscr: "PrintScreen",
  "print screen": "PrintScreen",
  pause: "Pause",
  "-": "Minus",
  _: "Minus",
  "–": "Minus",
  "=": "Equal",
  "+": "Equal",
  "[": "BracketLeft",
  "{": "BracketLeft",
  "]": "BracketRight",
  "}": "BracketRight",
  "\\": "Backslash",
  "|": "Backslash",
  ";": "Semicolon",
  ":": "Semicolon",
  "'": "Quote",
  '"': "Quote",
  ",": "Comma",
  "<": "Comma",
  ".": "Period",
  ">": "Period",
  "/": "Slash",
  "?": "Slash",
  "`": "Backquote",
  "~": "Backquote",
};

// KLE property names, longest-first where prefixes overlap (rx before r before x).
const KLE_PROPS =
  "rx|ry|x2|y2|w2|h2|r2|x|y|w|h|r|a|c|t|f2|f|p|d|g|n|sm|sb|st|fa|name|author|notes|background|backcolor";
const UNQUOTED_PROP = new RegExp(`([{,]\\s*)(${KLE_PROPS})\\s*:`, "g");

/**
 * Parse KLE text leniently. The site's "raw data" tab emits rows without the outer [] and
 * with unquoted property keys; hand-copied text often carries trailing commas (including one
 * at end of input, which must be stripped BEFORE the bracket wrap or the wrapped candidate
 * ends in ",]"). Downloaded JSON is strict and hits the first candidate unrewritten.
 */
export function lenientKLE(text: string): KLEData {
  const relaxed = text
    .replace(UNQUOTED_PROP, '$1"$2":')
    .replace(/,(\s*[\]}])/g, "$1")
    .replace(/,\s*$/, "");
  for (const candidate of [text, relaxed, `[${text}]`, `[${relaxed}]`]) {
    try {
      return JSON.parse(candidate) as KLEData;
    } catch {
      // try the next, more forgiving form
    }
  }
  throw new Error("Couldn't parse this as KLE JSON or KLE raw data.");
}

/**
 * Split a raw KLE legend into its non-empty, trimmed parts. `<br>` inside a part is a line
 * break within ONE legend ("Scroll<br>lock" is the words "Scroll lock"), not a slot separator:
 * splitting on it would mint two false parts ("Back<br>Space" would read as a Space key).
 */
function legendParts(raw: unknown): string[] {
  return String(raw ?? "")
    .replace(/<br\s*\/?>/gi, " ")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Infer a KeyboardEvent.code from a KLE legend. The first two non-empty parts are the shift/base
 * slots and carry the key's identity; later parts are front/side legends (an embedded keypad's
 * digit, "SysReq") and must not outrank them - the Kinesis comma key "<\n,\n\n\n2" is Comma, not
 * Digit2. Within the main parts a single letter/digit still wins first (the key's true base char,
 * so "!\n1" gives Digit1 and '"\n2' is not read as Quote); the trailing parts are a last resort
 * so an exotic upload keeps its old resolution. A base-slot "#" is the ISO hash key (the physical
 * left-of-Enter Backslash) - everywhere else "#" rides shift over a digit, which the ordered scan
 * resolves first. Returns null when nothing maps.
 */
export function legendToCode(raw: unknown): string | null {
  const parts = legendParts(raw);
  // the ISO pair, shift-over-base ONLY (a lone "#" or "<" is a macro cap, not an ISO key -
  // coding it would mint a code twin whose last-wins legend entry breaks the real key's char):
  // base "#" is the hash key (the physical left-of-Enter Backslash), base "<" the continental
  // 102nd key (IntlBackslash). Everywhere else these chars ride shift over another base.
  if (parts.length > 1) {
    if (parts[1] === "#") return "Backslash";
    if (parts[1] === "<") return "IntlBackslash";
  }
  const scan = (slice: string[]): string | null => {
    for (const p of slice) {
      if (/^[A-Za-z]$/.test(p)) return `Key${p.toUpperCase()}`;
      if (/^[0-9]$/.test(p)) return `Digit${p}`;
    }
    for (const p of slice) {
      if (/^F([1-9]|1[0-2])$/i.test(p)) return p.toUpperCase();
      const c = L2C[p.toLowerCase()];
      if (c) return c;
    }
    return null;
  };
  return scan(parts.slice(0, 2)) ?? scan(parts.slice(2));
}

/** Codes the importer fabricates for keys it can't map. Real KeyboardEvent codes never look like this. */
export function isPlaceholderCode(code: string): boolean {
  return /^x\d+$/.test(code);
}

/**
 * The char a key emits per its KLE legend. KLE writes "shift\nbase", so the legend's base
 * part (second single-char part, else the only one) wins; the QWERTY table is the fallback
 * for legends with no usable single-char part. Legend-first matters for non-QWERTY files:
 * AZERTY "2\né" infers code Digit2 from the shift part, but the key emits "é", not "2".
 * Keys whose code is neither char-emitting nor a placeholder (Esc, F1, Space, modifiers)
 * never map; placeholder-coded keys keep their single-char legends (e.g. "ö").
 */
function legendChar(legend: string, code: string): string | null {
  const known = QWERTY_CODE_TO_CHAR[code];
  if (!known && !isPlaceholderCode(code)) return null;
  const singles = legendParts(legend).filter((p) => [...p].length === 1);
  const base = singles[1] ?? singles[0];
  if (base) return base.toUpperCase();
  return known ?? null;
}

interface ParsedKLE {
  keys: KeyGeometry[];
  /** Raw KLE legend per key, index-aligned with keys. */
  legends: string[];
}

/**
 * Walk KLE rows into geometry plus the raw legend per key. Property objects nudge a running
 * cursor and set the rotation cluster; strings are keys. Codes are inferred from legends
 * (repeats are kept, since several physical keys may share a code); blank/unmappable keys
 * get a unique placeholder.
 */
export function parseKLEDetailed(rows: KLEData): ParsedKLE {
  const keys: KeyGeometry[] = [];
  const legends: string[] = [];
  const cl = { r: 0, rx: 0, ry: 0 };
  const cur = { x: 0, y: 0, w: 1, h: 1 };
  // KLE files leave the spacebar legend blank, so it would otherwise get a
  // placeholder code and a Space binding (Jump) would render nowhere. The one
  // blank key at least 5u wide is the space bar; claim it once (the
  // importer owns codes, the vendored source bytes stay untouched).
  let spaceClaimed = false;
  for (const row of rows) {
    if (!Array.isArray(row)) continue; // a metadata object
    for (const item of row) {
      if (typeof item === "object" && item !== null) {
        if (item.r !== undefined) cl.r = item.r;
        if (item.rx !== undefined) {
          cl.rx = item.rx;
          cur.x = cl.rx;
          cur.y = cl.ry;
        }
        if (item.ry !== undefined) {
          cl.ry = item.ry;
          cur.x = cl.rx;
          cur.y = cl.ry;
        }
        if (item.x !== undefined) cur.x += item.x;
        if (item.y !== undefined) cur.y += item.y;
        if (item.w !== undefined) cur.w = item.w;
        if (item.h !== undefined) cur.h = item.h;
      } else {
        const w = cur.w || 1;
        const code = legendToCode(item) ?? (!spaceClaimed && w >= 5 ? "Space" : `x${keys.length}`);
        if (code === "Space") spaceClaimed = true;
        const k: KeyGeometry = { code, x: cur.x, y: cur.y, w, h: cur.h || 1 };
        if (cl.r) {
          k.r = cl.r;
          k.rx = cl.rx;
          k.ry = cl.ry;
        }
        keys.push(k);
        legends.push(String(item ?? ""));
        cur.x += cur.w;
        cur.w = 1;
        cur.h = 1;
      }
    }
    cur.y += 1;
    cur.x = cl.rx;
  }
  return { keys, legends };
}

// KLE legends name modifiers side-lessly ("Shift"), so L2C maps them all to the Left code; which
// physical side a key sits on is geometry, resolved by the recode pass below.
const LEFT_TO_RIGHT: Record<string, string> = {
  ShiftLeft: "ShiftRight",
  ControlLeft: "ControlRight",
  AltLeft: "AltRight",
  MetaLeft: "MetaRight",
};

/**
 * Give right-side modifiers their *Right codes. The side is the key's split-region tag where the
 * board has one (a split's spacebar can sit on one thumb cluster, skewing any midpoint test), else
 * which side of the spacebar's centre the key sits - the same signal findModifierCluster uses.
 * A board with neither signal (no Space, no regions) stays as parsed.
 */
function assignRightModifierCodes(keys: KeyGeometry[]): void {
  const space = keys.find((k) => k.code === "Space");
  const split = keys.some((k) => k.region != null);
  if (!space && !split) return;
  const mid = space ? space.x + space.w / 2 : 0;
  for (const k of keys) {
    const right = LEFT_TO_RIGHT[k.code];
    if (!right) continue;
    const onRight = k.region != null ? k.region === "R" : space != null && k.x + k.w / 2 > mid;
    if (onRight) k.code = right;
  }
}

// A numpad key's identity by its primary (top-left slot) legend part. The digits collide with the
// main number row; the operators with the punctuation row; Enter with Enter - which is why the
// block needs geometric detection plus this table rather than legends alone.
const NUMPAD_CODES: Record<string, string> = {
  "0": "Numpad0",
  "1": "Numpad1",
  "2": "Numpad2",
  "3": "Numpad3",
  "4": "Numpad4",
  "5": "Numpad5",
  "6": "Numpad6",
  "7": "Numpad7",
  "8": "Numpad8",
  "9": "Numpad9",
  "/": "NumpadDivide",
  "*": "NumpadMultiply",
  "-": "NumpadSubtract",
  "+": "NumpadAdd",
  ".": "NumpadDecimal",
  enter: "NumpadEnter",
  "num lock": "NumLock",
};

// A real numpad block is at least this many keys; a small digit-labeled satellite/macro column
// must keep its char codes (recoding it Numpad* would un-paint the char bindings that
// twin-paint it, since char tokens exclude Numpad-coded keys by design).
const NUMPAD_MIN_KEYS = 10;

/**
 * Recode a full-size board's numpad block: the keys behind a full-height vertical gap whose
 * primary legend parts are ALL numpad vocabulary and that is numpad-sized. The content gate is
 * what rejects a TKL's nav cluster (Insert/Home/arrows behind the same kind of gap); the size
 * gate rejects digit macro columns; split boards (region-tagged) are skipped outright - their
 * halves are not numpads. The gap's width is deliberately unbounded: the vendored full-size
 * boards use 0.25u but community exports space the block past the 0.45u split threshold, and
 * the split skip plus both gates already carry the safety. Candidates are tried right to left.
 */
function assignNumpadCodes(keys: KeyGeometry[], legends: string[], width: number): void {
  if (keys.some((k) => k.region != null)) return;
  const gaps = verticalGaps(keys, width);
  for (let g = gaps.length - 1; g >= 0; g--) {
    const gap = gaps[g];
    if (!gap) continue;
    const block: { key: KeyGeometry; code: string }[] = [];
    let allNumpad = true;
    for (let i = 0; i < keys.length && allNumpad; i++) {
      const key = keys[i];
      if (!key || key.x < gap.end - 1e-9) continue;
      const primary = legendParts(legends[i] ?? "")[0] ?? "";
      const code = NUMPAD_CODES[primary.toLowerCase()];
      if (code) block.push({ key, code });
      else allNumpad = false;
    }
    if (!allNumpad || block.length < NUMPAD_MIN_KEYS) continue;
    for (const { key, code } of block) key.code = code;
    return;
  }
}

/** A hand-curated code override for one key whose legend carries no code signal. */
export interface CodeOverride {
  /** Key index in the vendored source's key order. */
  index: number;
  /** The raw KLE legend expected at that index. */
  legend: string;
  code: string;
}

/**
 * Apply hand-curated code overrides atop the importer's own inference, for the few keys whose
 * legend genuinely carries no code signal (the ISO 102nd key's "|\" is byte-identical to the ANSI
 * Backslash key; the Kinesis up-arrow uses KLE's standard Shift glyph). Codes only, never
 * geometry. Each entry is keyed (index, exact raw legend): a re-vendored source that moves or
 * relabels the key fails loudly here instead of miscoding silently.
 */
export function applyCodeOverrides(
  keys: KeyGeometry[],
  legends: string[],
  overrides: CodeOverride[],
): void {
  for (const o of overrides) {
    const key = keys[o.index];
    const legend = legends[o.index];
    if (!key || legend !== o.legend) {
      throw new Error(
        `code override ${o.code}@${o.index}: expected legend ${JSON.stringify(o.legend)}, ` +
          `found ${JSON.stringify(legend ?? null)} - the vendored source changed; re-derive the override`,
      );
    }
    key.code = o.code;
  }
}

export interface KLEImport {
  keyboard: Keyboard;
  /** Default Keyboard Layout: the char each key emits, per the file's own legends. */
  keyboardLayout: KeyboardLayout;
}

/** Parse a KLE document into a Keyboard plus its default Keyboard Layout, normalized to a (0,0) origin. */
export function importKLE(
  rows: KLEData,
  meta: { id: string; name: string },
  codeOverrides?: CodeOverride[],
): KLEImport {
  const { keys, legends } = parseKLEDetailed(rows);
  if (!keys.length) throw new Error("No keys found in this KLE data.");
  const size = normalizeOrigin(keys);
  assignSplitRegions(keys, size.width);
  // code refinement, in order: side-aware modifiers, the numpad block, then any curated
  // residue - each reads signals only available here (normalized geometry, regions, legends)
  assignRightModifierCodes(keys);
  assignNumpadCodes(keys, legends, size.width);
  if (codeOverrides) applyCodeOverrides(keys, legends, codeOverrides);
  const map: Record<string, string> = {};
  keys.forEach((k, i) => {
    const ch = legendChar(legends[i] ?? "", k.code);
    if (ch != null) map[k.code] = ch;
  });
  return {
    keyboard: {
      id: meta.id,
      name: meta.name,
      width: size.width,
      height: size.height,
      keys,
    },
    keyboardLayout: { id: `${meta.id}-legends`, name: `${meta.name} legends`, map },
  };
}
